import * as React from "react";
import {
  TextField,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  CircularProgress,
} from "@mui/material";
import ModalInc from "../../../../shared-ui/ModalInc";
import ButtonInc from "../../../../shared-ui/ButtonInc";
import AlertDialog from "../../../../components/AlertDialog/index.es6";
import { POLICY_MAKER_CHECK_TYPES } from "../../constants";

function buildFormState(check) {
  var firstType = POLICY_MAKER_CHECK_TYPES[0] || "";
  if (!check) {
    return {
      type: firstType,
      label: "",
      sequence: "",
      allow_to_skip: false,
    };
  }
  return {
    type: check.type || firstType,
    label: check.label || "",
    sequence: check.sequence != null ? String(check.sequence) : "",
    allow_to_skip: !!check.allow_to_skip,
  };
}

export default function AddEditPolicyMakerCheck(props) {
  var isEdit = !!props.existingCheck;
  var open = props.open;
  var typeOptions = POLICY_MAKER_CHECK_TYPES || [];
  var noTypes = typeOptions.length === 0;

  var [form, setForm] = React.useState(buildFormState(props.existingCheck));
  var [errors, setErrors] = React.useState({});

  React.useEffect(
    function() {
      if (open) {
        setForm(buildFormState(props.existingCheck));
        setErrors({});
      }
    },
    [open]
  );

  function set(field, value) {
    setForm(function(prev) {
      var next = Object.assign({}, prev);
      next[field] = value;
      return next;
    });
    setErrors(function(prev) {
      if (!prev[field]) return prev;
      var next = Object.assign({}, prev);
      delete next[field];
      return next;
    });
  }

  // Returns the label of an existing check already using seqNum (excluding the
  // check currently being edited), or null when the sequence is free.
  function duplicateSequenceLabel(seqNum) {
    var existing = props.existingChecks || [];
    var currentGuid = props.existingCheck && props.existingCheck.guid;
    for (var i = 0; i < existing.length; i++) {
      var c = existing[i];
      if (currentGuid && c.guid === currentGuid) {
        continue;
      }
      if (Number(c.sequence) === seqNum) {
        return c.label || c.type || "#" + seqNum;
      }
    }
    return null;
  }

  function validate() {
    var errs = {};

    if (noTypes) {
      errs.type = "No approval types available.";
    } else if (!form.type) {
      errs.type = "Type is required";
    }

    if (!form.label || !form.label.trim()) {
      errs.label = "Label is required";
    }

    if (form.sequence === "" || form.sequence == null) {
      errs.sequence = "Sequence is required";
    } else {
      var seqNum = Number(form.sequence);
      if (isNaN(seqNum) || seqNum < 0) {
        errs.sequence = "Sequence must be a non-negative number";
      } else {
        var dupLabel = duplicateSequenceLabel(seqNum);
        if (dupLabel) {
          errs.sequence =
            "Sequence " + seqNum + ' is already used by "' + dupLabel + '"';
        }
      }
    }

    return errs;
  }

  function handleSave() {
    var errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    var payload = {
      type: form.type,
      label: form.label.trim(),
      sequence: Number(form.sequence),
      allow_to_skip: !!form.allow_to_skip,
    };
    if (isEdit && props.existingCheck.guid) {
      payload.guid = props.existingCheck.guid;
    }
    props.onSave(payload);
  }

  function handleSaveSuccess() {
    props.onSaveSuccess();
  }

  function handleSaveError() {
    props.onSaveErrorReset();
  }

  var modalTitle = isEdit
    ? "Edit Policy Maker Check: " + (props.existingCheck.label || "")
    : "Add Policy Maker Check";

  return (
    <>
      <AlertDialog
        custom
        show={props.saveLoading}
        size="sm"
        style={{ marginTop: "0", top: "30vh" }}
        showCancel={false}
        showConfirm={false}
        showTitle={false}
        confirmBtnCssClass
      >
        <CircularProgress />
        <h2>Saving policy maker check...</h2>
      </AlertDialog>

      <AlertDialog
        success
        show={props.saveSuccess}
        size="sm"
        title={"Policy maker check saved successfully"}
        style={{ marginTop: "0", top: "30vh" }}
        onConfirm={handleSaveSuccess}
        confirmBtnText={"OK"}
        showCancel={false}
        confirmBtnCssClass
      />

      <AlertDialog
        danger
        show={!!props.saveError}
        title={"Error saving policy maker check"}
        onConfirm={handleSaveError}
        confirmBtnText={"Try again"}
        showCancel={false}
        style={{ marginTop: "0", top: "30vh" }}
        confirmBtnCssClass
      >
        {props.saveError
          ? typeof props.saveError === "string"
            ? props.saveError
            : props.saveError.message || "Unknown error"
          : ""}
      </AlertDialog>

      <ModalInc
        modalTitle={modalTitle}
        open={open}
        close={props.onClose}
        fullWidth
        maxWidth="sm"
      >
        <Grid container spacing={3} style={{ marginTop: 4 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              label="Type"
              value={form.type}
              onChange={function(e) {
                set("type", e.target.value);
              }}
              variant="outlined"
              disabled={noTypes}
              error={!!errors.type}
              helperText={
                errors.type
                  ? errors.type
                  : noTypes
                  ? "No approval types available."
                  : "Approval type for this maker-checker step."
              }
            >
              {typeOptions.map(function(t) {
                return (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                );
              })}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Label"
              value={form.label}
              onChange={function(e) {
                set("label", e.target.value);
              }}
              variant="outlined"
              error={!!errors.label}
              helperText={errors.label || "Descriptive label for this approval step."}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Sequence"
              value={form.sequence}
              onChange={function(e) {
                set("sequence", e.target.value);
              }}
              variant="outlined"
              error={!!errors.sequence}
              helperText={
                errors.sequence || "Execution order of this step (must be unique)."
              }
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.allow_to_skip}
                  onChange={function(e) {
                    set("allow_to_skip", e.target.checked);
                  }}
                  color="primary"
                />
              }
              label="Allow this step to be skipped"
            />
          </Grid>

          <Grid item xs={12}>
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                marginTop: "10px",
                marginBottom: "10px",
              }}
            >
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                btnType="secondary"
                onClick={props.onClose}
              >
                Cancel
              </ButtonInc>
              <ButtonInc
                variant="contained"
                color="primary"
                size="large"
                disabled={noTypes}
                loading={props.saveLoading}
                onClick={handleSave}
              >
                {isEdit ? "Save Changes" : "Add Check"}
              </ButtonInc>
            </div>
          </Grid>
        </Grid>
      </ModalInc>
    </>
  );
}
